import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";
import {
  formatRoundTime,
  listAdmittedPatientsForDoctorRound,
  mapWebPatientToAdmissionRow,
  toApiTime,
} from "./doctorRoundService";

const WEB_BASE = "/api/v1/web/intake-output";

export {
  formatRoundTime,
  listAdmittedPatientsForDoctorRound,
  mapWebPatientToAdmissionRow,
  toApiTime,
};

export const listIntakeOutputByAdmission = async (admissionId) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: { admissionId },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load Intake & Output records")
    );
  }
};

export const getIntakeOutputById = async (inOutId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${inOutId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load Intake & Output record")
    );
  }
};

export const createIntakeOutput = async (payload) => {
  try {
    const response = await axiosInstance.post(WEB_BASE, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to save Intake & Output record")
    );
  }
};

export const updateIntakeOutput = async (inOutId, payload) => {
  try {
    const response = await axiosInstance.put(`${WEB_BASE}/${inOutId}`, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update Intake & Output record")
    );
  }
};

const toLong = (value) => {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const toStr = (value) => (value == null ? "" : String(value));

const sumFields = (values) =>
  values.reduce((acc, v) => acc + toLong(v), 0);

/** Build create/update API payload from RHF form + patient + userId. */
export const mapFormToSavePayload = (formData, patient, userId, { isUpdate } = {}) => {
  const oralNg = toLong(formData.oral);
  const othersIn = toLong(formData.intakeOthers);
  const totalPerenteral = toLong(formData.totalParenteral);
  const urine = toLong(formData.urine);
  const drain = toLong(formData.drain);
  const aspiration = toLong(formData.aspiration);
  const othersOut = toLong(formData.outputOthers);

  let totalIn = toLong(formData.intakeTotal);
  if (!totalIn) {
    totalIn = sumFields([oralNg, formData.ng, formData.iv, othersIn, totalPerenteral]);
  }

  let totalOut = toLong(formData.outputTotal);
  if (!totalOut) {
    totalOut = sumFields([urine, drain, aspiration, othersOut]);
  }

  const base = {
    date: formData.date,
    time: toApiTime(formData.time),
    doctorId: Number(formData.doctor?.value ?? formData.doctor?.id),
    oralNg,
    ng: toStr(formData.ng),
    iv: toStr(formData.iv),
    totalPerenteral,
    othersIn,
    totalIn,
    urine,
    aspiration,
    drain,
    othersOut,
    totalOut,
    remark: toStr(formData.remark),
  };

  if (isUpdate) {
    return {
      ...base,
      updatedBy: userId,
    };
  }

  return {
    regId: Number(patient.regId),
    admissionId: Number(patient.admissionId),
    ...base,
    addedBy: userId,
    updatedBy: userId,
  };
};

/** Map API detail row into RHF form field values (excluding doctor option). */
export const mapRecordToFormValues = (row) => {
  if (!row) return null;
  return {
    date: row.date || new Date().toISOString().split("T")[0],
    time: formatRoundTime(row.time) || "",
    oral: row.oralNg != null ? String(row.oralNg) : "0",
    ng: row.ng != null ? String(row.ng).trim() || "0" : "0",
    intakeOthers: row.othersIn != null ? String(row.othersIn) : "0",
    iv: row.iv != null ? String(row.iv).trim() || "0" : "0",
    totalParenteral:
      row.totalPerenteral != null ? String(row.totalPerenteral) : "0",
    intakeTotal: row.totalIn != null ? String(row.totalIn) : "0",
    urine: row.urine != null ? String(row.urine) : "0",
    drain: row.drain != null ? String(row.drain) : "0",
    aspiration: row.aspiration != null ? String(row.aspiration) : "0",
    outputOthers: row.othersOut != null ? String(row.othersOut) : "0",
    outputTotal: row.totalOut != null ? String(row.totalOut) : "0",
    remark: row.remark || "",
    addedBy: row.addedByName || row.userName || "",
    doctorId: row.doctorId,
    doctorName: row.doctorName,
  };
};

/** Normalize list row for the history table. */
export const mapListRowForTable = (row) => ({
  id: row.inOutId,
  inOutId: row.inOutId,
  date: row.date || "—",
  time: formatRoundTime(row.time) || "—",
  doctor: row.doctorName || "—",
  intakeTotal: row.totalIn != null ? String(row.totalIn) : "—",
  outputTotal: row.totalOut != null ? String(row.totalOut) : "—",
  addedBy: row.addedByName || row.userName || "—",
});
